import { z } from "zod";

// ============================================================================
// FILE VALIDATION HELPERS
// ============================================================================

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

/**
 * Schema for an uploaded image (after successful upload to backend)
 * Used in upload components to track uploaded state
 */
export const uploadedImageSchema = z.object({
  key: z.string(),
  // Display-only URLs, kept for rendering the editor. NEVER submitted (only
  // `key` is sent), and the backend GET may return relative/CDN paths — so do
  // NOT require an absolute URL here, or edits of such products fail validation.
  originalUrl: z.string(),
  thumbnailUrl: z.string(),
});

export type UploadedImage = z.infer<typeof uploadedImageSchema>;

// ============================================================================
// MEDIA ITEM TYPES (New API)
// ============================================================================

/**
 * Media item request for product submission (new API format)
 */
export const mediaItemRequestSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('image'), imageKey: z.string() }),
  z.object({
    type: z.literal('video'),
    url: z.string().url(),
    scalePercent: z.number().min(100).max(200).optional(),
    offsetX: z.number().min(-50).max(50).optional(),
    offsetY: z.number().min(-50).max(50).optional(),
  }),
  // Embed player src from trusted platforms
  z.object({
    type: z.literal('iframe'),
    src: z.string().url(),
    aspectRatio: z.number().positive(),
    scalePercent: z.number().min(100).max(200).optional(),
    offsetX: z.number().min(-50).max(50).optional(),
    offsetY: z.number().min(-50).max(50).optional(),
  }),
  z.object({ type: z.literal('text'), content: z.string().max(5000) }),
  z.object({ type: z.literal('gallery'), imageKeys: z.array(z.string()) }),
]);

export type MediaItemRequest = z.infer<typeof mediaItemRequestSchema>;

// ============================================================================
// STATUS TAGS
// ============================================================================

export const STATUS_TAG_OPTIONS = [
  { value: "investment", label: "💰 Ищу инвестиции", description: "Активно ищу инвесторов" },
  { value: "feedback", label: "🔥 Хочу обратную связь", description: "Нужны отзывы и предложения" },
  { value: "partnerships", label: "🤝 Открыт к партнерствам", description: "Рассматриваю сотрудничество" },
  { value: "mentors", label: "🎓 Ищу менторов", description: "Нужна помощь опытных предпринимателей" },
  { value: "hiring", label: "👨‍💻 Нанимаем", description: "Ищем специалистов в команду" },
] as const;

export const statusTagEnum = z.enum([
  "investment",
  "feedback",
  "partnerships",
  "mentors",
  "hiring",
]);

// ============================================================================
// PRODUCT CATEGORIES
// ============================================================================

/**
 * Category data from backend API
 */
export interface CategoryDto {
  id: number
  slug: string
  name: string
  description: string | null
  parentId: number | null
  productCount: number
  sortOrder: number
}

// ============================================================================
// FORM VALIDATION SCHEMA
// ============================================================================

/**
 * Main product submission form schema
 * Used for client-side validation with react-hook-form
 */
export const productSubmissionFormSchema = z.object({
  // Slug (URL path)
  slug: z
    .string()
    .min(1, "Slug обязателен")
    .max(60, "Slug не должен превышать 60 символов")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Только строчные латинские буквы, цифры и дефис")
    .trim(),

  // Basic Information
  name: z
    .string()
    .min(1, "Название должно содержать минимум 1 символ")
    .max(50, "Название не должно превышать 50 символов")
    .trim(),

  tagline: z
    .string()
    .min(6, "Слоган должен содержать минимум 6 символов")
    .max(100, "Слоган не должен превышать 100 символов")
    .trim(),

  description: z
    .string()
    .min(50, "Описание должно содержать минимум 50 символов")
    .max(1000, "Описание не должно превышать 1000 символов")
    .trim(),

  websiteUrl: z
    .string()
    .url("Введите корректный URL (например: https://example.com)")
    .trim()
    .optional()
    .or(z.literal("")),

  // Media Files - stored as uploaded image info (after upload to backend)
  logo: z.object({
    key: z.string(),
    originalUrl: z.string().url(),
    thumbnailUrl: z.string().url(),
  }, {
    message: "Загрузите логотип"
  }),

  screenshots: z
    .array(uploadedImageSchema)
    .min(2, "Загрузите минимум 2 изображения")
    .max(5, "Максимум 5 скриншотов")
    .optional(),

  // Media items (new API format) - replaces screenshots
  media: z
    .array(mediaItemRequestSchema)
    .max(10, "Максимум 10 медиа элементов")
    .optional(),

  // Preview images for featured/list display (Steam-style)
  previewImages: z
    .array(z.string())
    .max(5, "Максимум 5 изображений")
    .optional(),

  // Product Details
  highlights: z
    .array(
      z
        .string()
        .min(5, "Каждый пункт должен содержать минимум 5 символов")
        .max(100, "Каждый пункт не должен превышать 100 символов")
        .trim()
    )
    .min(3, "Добавьте минимум 3 ключевых особенности")
    .max(5, "Максимум 5 ключевых особенностей")
    .optional()
    .default([]),

  categoryIds: z
    .array(z.number())
    .min(1, "Выберите минимум 1 категорию")
    .max(3, "Максимум 3 категории"),

  statusTags: z.array(statusTagEnum).min(1, "Выберите минимум 1 вариант"),

  // Launch date (optional)
  launchDate: z
    .string()
    .optional(),
});

/**
 * Schema for validating the data sent to the backend API
 * (FormData will be created from this)
 */
export const productSubmissionApiSchema = productSubmissionFormSchema;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ProductSubmissionFormData = z.infer<typeof productSubmissionFormSchema>;
export type StatusTag = z.infer<typeof statusTagEnum>;

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates if a value is a valid URL
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates if a file is an image
 */
export function isImageFile(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.includes(file.type);
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
}

/**
 * Gets file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
}
